import { Outlet, useLocation } from 'react-router';
import styled, { useTheme } from 'styled-components';
import { TopBar } from '../components/TopBar';
import type { MenuPage } from '../topBarMenuPages/MenuPage';
import { Suspense } from 'react';
import { Spinner } from '../components/Spinner/Spinner';
import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { ErrorView } from '../components/ErrorView';

export const MainLayout: React.FC<{
  topBarMenuPages: MenuPage[];
  managerView?: boolean;
}> = ({ topBarMenuPages, managerView }) => {
  const theme = useTheme();
  const { reset } = useQueryErrorResetBoundary();
  const location = useLocation();

  return (
    <Layout>
      <TopBar topBarMenuPages={topBarMenuPages} managerView={managerView} />
      <Content>
        <Suspense
          fallback={
            <div
              style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}
            >
              <Spinner size="48px" color={theme.colors.prototype.primary} />
            </div>
          }
        >
          <ErrorBoundary
            onReset={reset}
            resetKeys={[location.pathname]}
            fallbackRender={({ error, resetErrorBoundary }) => (
              <ErrorView error={error} onTryAgain={resetErrorBoundary} />
            )}
          >
            <Outlet />
          </ErrorBoundary>
        </Suspense>
      </Content>
    </Layout>
  );
};
const Layout = styled.div`
  min-height: 100%;
  /* Full-bleed page sections break out with margin: calc(50% - 50vw); 50vw counts the
     vertical scrollbar while 50% does not, so they overhang by one scrollbar width and
     force a page-level horizontal scrollbar. Clip that overhang without creating a
     scroll container (so vertical scroll and the fixed TopBar are unaffected). */
  overflow-x: clip;
`;

const Content = styled.div`
  --layout-offset-top: 78px;
  --layout-offset-x: 48px;
  --layout-offset-bottom: 48px;
  width: 100%;
  max-width: 1600px;
  margin: 0 auto;
  padding: var(--layout-offset-top) var(--layout-offset-x) var(--layout-offset-bottom);

  @media (max-width: 768px) {
    --layout-offset-top: 72px;
    --layout-offset-x: 16px;
    --layout-offset-bottom: 16px;
  }
`;
